import * as React from 'react';
import { useSelector } from 'react-redux';
import { Typography, TextInput, Button, Box, Flex, color } from '@kistech/kis-ui';
import { selectors as authSelectors, actions as authActions } from 'store/auth';
import { KioskControlCenter } from 'components/KioskControlCenter';
import { validateEmail } from 'utils/validation';
import { useActions } from 'hooks/useActions';
import { useStyles } from './styled';
import { Grid, Typography as MuiTypography, Box as MuiBox } from '@mui/material';

const INVALID_EMAIL = 'Invalid email format';

const ForgotPasswordView: React.FC = () => {
  // ACTION CREATORS AND SELECTORS

  const { requestResetPassword } = useActions({ requestResetPassword: authActions.requestResetPassword });
  const error = useSelector(authSelectors.selectActionError);
  const updating = useSelector(authSelectors.selectActionPending);
  const resetSuccess = useSelector(authSelectors.selectActionSuccessful);
  const classes = useStyles();

  // EMAIL INPUT STATES
  const [email, setEmail] = React.useState('');

  // INVALID MESSAGE WHEN EMAIL INCORRECT FORMAT
  const [invalidMsg, setInvalidMsg] = React.useState<string | null>(null);

  // ON UPDATE OF ERROR - SET THE ERROR AS INVALID MESSAGE
  React.useEffect(() => {
    if (!updating) {
      setInvalidMsg(error);
    }
  }, [error, updating]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // EMAIL VALIDATION
  const emailValidationFn = async (value: string | number | undefined) => {
    // VALIDATION SCHEMA
    const schema = validateEmail(INVALID_EMAIL);

    // VALIDATE FN
    const result = await schema.validate(value).catch((err) => err);

    // IF THERE WAS ERROR MESSAGE - SET IT TO INVALID MESSAGE
    setInvalidMsg(result && result.message);

    if (result && !result.message) {
      // FIRES UPDATE ADMIN ASYNC FN
      requestResetPassword(email);
    }
  };

  // INPUT CHANGE
  const onInputChange = (setFn: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    // IF INVALID MESSAGE EXISTS SET IT TO NULL
    if (invalidMsg) setInvalidMsg(null);

    // FIRES THE APPROPRIATE FN: SETPASSWORD | SETCONFIRMPASSWORD WITH VALUE
    setFn(value);
  };

  // FORM SUBMIT
  const onSaveClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    emailValidationFn(email);
  };

  const buttonDisabled = !email.length || !!invalidMsg;
  return (
    <Grid container direction="row" className={classes.forgotPasswordOuterContainer}>
      {/* LEFT OF CARD */}
      <Grid item md={6} xl={6} lg={6} sm={12} xs={12} className={classes.forgotPasswordImageBlock}>
        <Flex fullHeight bg="light-gray">
          <KioskControlCenter />
        </Flex>
      </Grid>
      {/* RIGHT OF CARD - FORGOT PASSWORD FORM */}
      {!resetSuccess && (
        <Grid direction="column" item md={6} xl={6} lg={6} sm={12} xs={12} className={classes.forgotPasswordViewBlock}>
          <MuiBox className={classes.forgotPasswordTextBox}>
            <MuiTypography className={classes.forgotPasswordHeader}>Forgot Your Password?</MuiTypography>
            <MuiBox className={classes.forgotPasswordTextBorder} />
            <MuiTypography className={classes.forgotPasswordSubHeader}>
              Type your email below and we will send you a link to reset your password.
            </MuiTypography>
          </MuiBox>
          <form>
            {/* EMAIL INPUT */}
            <MuiBox className={classes.forgotPasswordEmailInput}>
              <TextInput
                onChange={(e) => onInputChange(setEmail, e.target.value)}
                isError={!!invalidMsg}
                validate={emailValidationFn}
                label="Email"
                type="email"
                placeholder="Your Email"
                fullWidth
              />
            </MuiBox>
            {/* ERROR MESSAGE */}
            {invalidMsg && !updating && (
              <Box flexOverride="none" margin="0 0 5" height={24}>
                <Typography size={16} color={color.RED}>
                  {invalidMsg}
                </Typography>
              </Box>
            )}
            {/* SUBMIT BUTTON */}
            <Button
              isDisabled={buttonDisabled}
              size="large"
              onClick={onSaveClick}
              fullWidth
              isLoading={updating}
              type="submit"
              className={classes.forgotPasswordSendEmailButton}
            >
              Send Email
            </Button>
          </form>
        </Grid>
      )}

      {resetSuccess && (
        <Grid direction="column" item md={6} xl={6} lg={6} sm={12} xs={12} className={classes.PasswordResetEmailBlock}>
          <Box flexOverride="none" margin="0 0 7">
            <MuiTypography className={classes.passwordResetHeader}>An email has been sent.</MuiTypography>
            <MuiBox className={classes.passwordResetDivider} />
            <MuiTypography className={classes.passwordResetSubHeader}>
              Please check your inbox. An email has been sent containing a link to reset your password. You can now
              close this tab and follow the instructions found in the email.
            </MuiTypography>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default ForgotPasswordView;
